import {
  ButtonProps,
  Popover as MuiPopover,
  PopoverOrigin as MuiPopoverOrigin,
  PopoverProps as MuiPopoverProps,
} from '@mui/material';
import React, { forwardRef, MouseEventHandler, ReactElement, ReactNode, useRef, useState } from 'react';
import { SxProps } from '../../utils';

type NativeDivProps = React.ComponentPropsWithoutRef<'div'>;

export interface PopoverBaseProps extends NativeDivProps, Pick<MuiPopoverProps, 'onClose'> {
  /**
   * The trigger element to open the popover.
   */
  // The type makes sure that the trigger element can accept the props that we will pass to it.
  children: ReactElement<Pick<ButtonProps, 'ref' | 'onClick' | 'onMouseEnter' | 'onMouseLeave'>>;
  /**
   * The content of the popover.
   * If a consumer is using uncontrolled popover (default) but needs the content to close the popover,
   * this prop can be a render function that will be passed `closePopover`.
   */
  Content: ((args: { closePopover: () => void }) => ReactNode) | ReactNode;
  /**
   * Always keep the children in the DOM. This prop can be useful in cases where something inside the menu needs to
   * stay mounted when the menu is closed (e.g. state management for a filter).
   *
   * This prop is prefixed with "dangerously" because its use often indicates antipatterns that could lead to
   * performance issues or other problems. For example, the use case with the filter state would be better handled by
   * storing that state outside of the popover, so it is not impacted by the mounting of the popover.
   *
   * ChronoUI primarily supports this property to ease transitioning to some new responsive patterns for teams.
   */
  dangerouslyKeepMounted?: MuiPopoverProps['keepMounted'];
  /**
   * Consumer can control the open/closed state of the popover.
   * By default, the popover manages this state internally.
   */
  open?: boolean;
  /**
   * The placement of the popover relative to the trigger element.
   * This prop is using the API used by the MUI Tooltip:
   * https://mui.com/material-ui/react-tooltip/#positioned-tooltips.
   * @default 'bottom-start'
   */
  placement?: 'bottom-start' | 'right-start';
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps;
  /**
   * The action that triggers the popover.
   * @default 'click'
   */
  trigger?: 'click' | 'hover';
}

const PLACEMENT_TO_ORIGINS = {
  'bottom-start': {
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' } as MuiPopoverOrigin,
    transformOrigin: { vertical: 'top', horizontal: 'left' } as MuiPopoverOrigin,
  },
  'right-start': {
    anchorOrigin: { vertical: 'top', horizontal: 'right' } as MuiPopoverOrigin,
    transformOrigin: { vertical: 'top', horizontal: 'left' } as MuiPopoverOrigin,
  },
};

/**
 * A lower-level popover component that can be used to build popover-based designs.
 */
export const PopoverBase = forwardRef<HTMLDivElement, PopoverBaseProps>(function PopoverBase(
  { children, Content, dangerouslyKeepMounted, open, onClose, placement = 'bottom-start', trigger = 'click', ...props },
  ref
) {
  // Consumer can control the open/closed state with `open`.
  // By default, the popover manages this state internally.
  const isControlled = open !== undefined;
  const isTriggerHover = !isControlled && trigger === 'hover';

  const [isOpenInternal, setIsOpenInternal] = useState(false);
  const openPopover = () => {
    setIsOpenInternal(true);
  };
  const closePopover = () => {
    setIsOpenInternal(false);
  };

  // Assert that the caller uses a single element as the trigger element.
  const child = React.Children.only(children);

  let triggerElement;
  const triggerRef = useRef(null);

  if (isControlled) {
    triggerElement = React.cloneElement(child, {
      ref: triggerRef,
    });
  } else if (trigger === 'hover') {
    triggerElement = React.cloneElement(child, {
      ref: triggerRef,
      onMouseEnter: (e) => {
        child.props.onMouseEnter?.(e);
        openPopover();
      },
      onMouseLeave: (e) => {
        child.props.onMouseLeave?.(e);
        closePopover();
      },
    });
  } else {
    triggerElement = React.cloneElement(child, {
      ref: triggerRef,
      onClick: ((e) => {
        if (child.props.onClick) {
          child.props.onClick(e);
        }
        openPopover();
      }) as MouseEventHandler<HTMLButtonElement>,
    });
  }

  // For hover mode, we need to keep the popover open when hovering on the content.
  const hoverPaperProps = isTriggerHover
    ? {
        onMouseEnter: () => {
          openPopover();
        },
        onMouseLeave: () => {
          closePopover();
        },
        style: { pointerEvents: 'auto' as const },
      }
    : {};

  return (
    <>
      {triggerElement}
      <MuiPopover
        {...props}
        ref={ref}
        open={isControlled ? open : isOpenInternal}
        anchorEl={triggerRef.current}
        anchorOrigin={PLACEMENT_TO_ORIGINS[placement].anchorOrigin}
        transformOrigin={PLACEMENT_TO_ORIGINS[placement].transformOrigin}
        role="dialog"
        keepMounted={dangerouslyKeepMounted}
        disableAutoFocus={isTriggerHover}
        disableEnforceFocus={isTriggerHover}
        disableRestoreFocus={isTriggerHover}
        PaperProps={hoverPaperProps}
        sx={{
          ...(isTriggerHover && { pointerEvents: 'none' }),
          ...props.sx,
        }}
        onClose={(...args) => {
          onClose?.(...args);
          if (!isControlled) {
            closePopover();
          }
        }}
      >
        {typeof Content === 'function' ? Content({ closePopover }) : Content}
      </MuiPopover>
    </>
  );
});
